import { useState } from "react";
import { X, CreditCard, Banknote, QrCode, PencilLine, RotateCcw, Check, Loader2 } from "lucide-react";
import { fmtBRL } from "../lib/constants";
import { useAuth } from "../lib/auth";

const PAY_OPTIONS = [
  { key: "pix_loja", label: "Pix Loja", icon: QrCode, color: "#059669" },
  { key: "pix_kevin", label: "Pix Kevin", icon: QrCode, color: "#0d9488" },
  { key: "dinheiro", label: "Dinheiro", icon: Banknote, color: "#ca8a04" },
  { key: "cartao", label: "Cartão", icon: CreditCard, color: "#4338ca" },
  { key: "anotar", label: "Anotar", icon: PencilLine, color: "#be123c" },
  { key: "devolvido", label: "Devolvido", icon: RotateCcw, color: "#7c3aed" },
];

const parseCurrency = (str) => {
  if (typeof str === "number") return str;
  return parseFloat(String(str || "").replace(/\./g, "").replace(",", ".")) || 0;
};

export default function ExitModal({ order, onClose, onComplete }) {
  const { user } = useAuth();
  const canSeeCost = user?.role === "admin";
  const [pay, setPay] = useState(null);
  const [desc, setDesc] = useState("");
  const [cost, setCost] = useState(order.costValue ?? "");
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    if (!pay || saving) return;
    setSaving(true);
    try {
      await onComplete(order.id, pay, desc, parseCurrency(cost));
    } finally {
      setSaving(false);
    }
  };

  const lucro = parseCurrency(order.total) - parseCurrency(cost);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      data-testid="exit-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-emerald-600 to-emerald-800 text-white flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold">Registrar saída</h3>
            <p className="text-xs text-white/70 mt-0.5">
              {order.orderNumber} · {order.clientName}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className={`grid gap-3 ${canSeeCost ? "grid-cols-2" : "grid-cols-1"}`}>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">
                Valor a receber
              </div>
              <div className="font-heading text-2xl font-extrabold text-slate-900 tabular-nums">
                R$ {fmtBRL(order.total)}
              </div>
            </div>
            {canSeeCost && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="text-[10px] uppercase font-semibold text-emerald-600 tracking-wider">
                  Lucro estimado
                </div>
                <div className="font-heading text-2xl font-extrabold text-emerald-800 tabular-nums">
                  R$ {fmtBRL(lucro)}
                </div>
              </div>
            )}
          </div>

          {canSeeCost && (
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider">
                Custo da peça / serviço (opcional)
              </label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">
                  R$
                </span>
                <input
                  inputMode="decimal"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  placeholder="0,00"
                  data-testid="exit-cost"
                  className="w-full h-10 rounded-lg border border-slate-300 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none text-right tabular-nums"
                />
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider mb-2">
              Forma de pagamento
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PAY_OPTIONS.map(({ key, label, icon: Icon, color }) => {
                const active = pay === key;
                return (
                  <button
                    key={key}
                    onClick={() => setPay(key)}
                    data-testid={`pay-${key}`}
                    className={`flex items-center gap-2 h-11 px-3 rounded-lg text-sm font-semibold transition-all border ${
                      active
                        ? "border-transparent text-white shadow-md -translate-y-[1px]"
                        : "border-slate-200 text-slate-700 hover:border-slate-300 bg-white"
                    }`}
                    style={active ? { background: color } : {}}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {pay === "cartao" && (
            <div className="animate-slide-down">
              <div className="text-[11px] uppercase font-semibold text-slate-500 tracking-wider mb-1">
                Descrição do cartão (opcional)
              </div>
              <input
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="Ex.: Débito Visa, Crédito 3x..."
                className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={!pay || saving}
            data-testid="confirm-exit"
            className="h-10 px-5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2 transition-all hover:-translate-y-[1px] hover:shadow-lg disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Confirmar entrega
          </button>
        </div>
      </div>
    </div>
  );
}
